import { useMemo, useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import { browseCategories, browseItems } from "@/mocks/home";

export default function BrowseSection() {
  const [activeCategory, setActiveCategory] = useState("全部");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return browseItems.filter((item) => {
      const matchCategory =
        activeCategory === "全部" || item.category === activeCategory;
      const matchKeyword =
        keyword === "" ||
        item.title.toLowerCase().includes(keyword) ||
        item.note.toLowerCase().includes(keyword) ||
        item.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, query]);

  return (
    <section id="browse" className="bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="分类浏览"
          title="按地区与题材筛，几秒锁定目标"
          description="所有条目都来自本页收录范围。用标签切换地区题材，或直接输入片名与关键词，筛选会在当前内容里即时生效。"
        />

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div
            role="tablist"
            aria-label="作品分类"
            className="flex flex-wrap gap-2"
          >
            {browseCategories.map((category) => {
              const active = category === activeCategory;
              return (
                <button
                  key={category}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveCategory(category)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm font-medium transition-colors cursor-pointer ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:w-72">
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"
              aria-hidden="true"
            ></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索片名或关键词"
              aria-label="搜索本页收录的作品"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <p className="mt-4 font-label text-xs text-foreground-500">
          当前筛选结果：
          <span className="mx-1 font-semibold text-primary-700">{filtered.length}</span>
          条
          {activeCategory !== "全部" ? ` · ${activeCategory}` : ""}
          {query.trim() ? ` · 关键词「${query.trim()}」` : ""}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {filtered.map((item) => (
              <article
                key={item.title}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 md:p-5"
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-label text-xs font-medium text-secondary-900">
                    {item.category}
                  </span>
                  <span className="flex items-center gap-1 font-label text-sm font-semibold text-accent-700">
                    <i className="ri-star-fill text-accent-600" aria-hidden="true"></i>
                    {item.rating}
                  </span>
                </div>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-0.5 text-xs text-foreground-500">{item.year} 年</p>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.note}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-background-200 bg-background-100 px-2.5 py-1 font-label text-xs text-foreground-600"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-inbox-2-line text-2xl" aria-hidden="true"></i>
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-800">
              没有匹配的作品
            </p>
            <p className="mt-1 text-sm text-foreground-500">
              换个关键词，或切换到「全部」分类再看看。
            </p>
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setActiveCategory("全部");
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}